import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { createMetric, getMetrics, updateMetric, deleteMetric as deleteMetricService, updateMetricOrder } from '../services/metricsService';
import { createLogEntry, getLogEntries, getLogEntriesForMetric, updateLogEntry, deleteLogEntry } from '../services/logEntriesService';

const MetricsContext = createContext();

export const useMetrics = () => {
  const context = useContext(MetricsContext);
  if (!context) {
    throw new Error('useMetrics must be used within a MetricsProvider');
  }
  return context;
};

export const MetricsProvider = ({ children }) => {
  const [metrics, setMetrics] = useState([]);
  const [logEntries, setLogEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();

  const loadMetrics = useCallback(async () => {
    if (!currentUser) return;
    
    setLoading(true);
    setError(null);
    try {
      const userMetrics = await getMetrics(currentUser.uid);
      setMetrics(userMetrics);
    } catch (err) {
      setError(err.message);
      console.error('Error loading metrics:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const loadLogEntries = useCallback(async () => {
    if (!currentUser) return;
    
    setLoading(true);
    setError(null);
    try {
      const userLogEntries = await getLogEntries(currentUser.uid);
      setLogEntries(userLogEntries);
    } catch (err) {
      setError(err.message);
      console.error('Error loading log entries:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Load metrics and log entries from Firebase on mount and when user changes
  useEffect(() => {
    if (currentUser) {
      loadMetrics();
      loadLogEntries();
    } else {
      setMetrics([]);
      setLogEntries([]);
    }
  }, [currentUser, loadMetrics, loadLogEntries]);

  const addMetric = async (metricData) => {
    if (!currentUser) {
      throw new Error('User must be authenticated');
    }

    setLoading(true);
    setError(null);
    try {
      const newMetric = await createMetric(metricData, currentUser.uid);
      setMetrics(prev => [newMetric, ...prev]);
      return newMetric;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateMetricById = async (id, updatedData) => {
    if (!currentUser) {
      throw new Error('User must be authenticated');
    }

    setLoading(true);
    setError(null);
    try {
      const updatedMetric = await updateMetric(id, updatedData, currentUser.uid);
      setMetrics(prev => 
        prev.map(metric => 
          metric.id === id ? { ...metric, ...updatedMetric } : metric
        )
      );
      return updatedMetric;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteMetric = async (id) => {
    if (!currentUser) {
      throw new Error('User must be authenticated');
    }

    setLoading(true);
    setError(null);
    try {
      await deleteMetricService(id, currentUser.uid);
      setMetrics(prev => prev.filter(metric => metric.id !== id));
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const addLogEntry = async (logEntryData) => {
    if (!currentUser) {
      throw new Error('User must be authenticated');
    }

    setLoading(true);
    setError(null);
    try {
      const newLogEntry = await createLogEntry(logEntryData, currentUser.uid);
      setLogEntries(prev => [newLogEntry, ...prev]);
      return newLogEntry;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getLogEntriesForMetricById = async (metricId) => {
    if (!currentUser) {
      throw new Error('User must be authenticated');
    }

    try {
      return await getLogEntriesForMetric(metricId, currentUser.uid);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updateLogEntryById = async (entryId, updatedData) => {
    if (!currentUser) {
      throw new Error('User must be authenticated');
    }

    setLoading(true);
    setError(null);
    try {
      const updatedEntry = await updateLogEntry(entryId, updatedData, currentUser.uid);
      setLogEntries(prev => 
        prev.map(entry => 
          entry.id === entryId ? { ...entry, ...updatedEntry } : entry
        )
      );
      return updatedEntry;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteLogEntryById = async (entryId) => {
    if (!currentUser) {
      throw new Error('User must be authenticated');
    }

    setLoading(true);
    setError(null);
    try {
      await deleteLogEntry(entryId, currentUser.uid);
      setLogEntries(prev => prev.filter(entry => entry.id !== entryId));
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteLogEntryByIdSilent = async (entryId) => {
    if (!currentUser) {
      throw new Error('User must be authenticated');
    }

    try {
      await deleteLogEntry(entryId, currentUser.uid);
      // Don't update UI immediately - let the caller handle it
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updateMetricOrderById = async (metricId, newOrder) => {
    if (!currentUser) {
      throw new Error('User must be authenticated');
    }

    setLoading(true);
    setError(null);
    try {
      await updateMetricOrder(metricId, newOrder, currentUser.uid);
      // Update local state
      setMetrics(prev => 
        prev.map(metric => 
          metric.id === metricId ? { ...metric, order: newOrder } : metric
        ).sort((a, b) => (a.order || 0) - (b.order || 0))
      );
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const checkDailyEntryExists = (metricId, date, excludeEntryId = null) => {
    return logEntries.some(entry => 
      entry.metricId === metricId && 
      entry.date === date && 
      entry.id !== excludeEntryId
    );
  };

  const value = {
    metrics,
    logEntries,
    loading,
    error,
    addMetric,
    updateMetric: updateMetricById,
    updateMetricOrder: updateMetricOrderById,
    deleteMetric,
    addLogEntry,
    updateLogEntry: updateLogEntryById,
    deleteLogEntry: deleteLogEntryById,
    deleteLogEntrySilent: deleteLogEntryByIdSilent,
    getLogEntriesForMetricById,
    checkDailyEntryExists,
    refreshMetrics: loadMetrics,
    refreshLogEntries: loadLogEntries
  };

  return (
    <MetricsContext.Provider value={value}>
      {children}
    </MetricsContext.Provider>
  );
};
